// Le relevé d'heures du mois : ce que chacun a fait, ce qui lui est dû, et ce
// qui manque encore.
//
// Rien n'est calculé ici. Le total d'un salarié, la pause retirée, les jours
// sans relevé viennent de @imp/shared/releve-heures, testé à part. L'écran ne
// fait que montrer — et rendre visible ce dont le calcul n'est pas sûr.

import { BOM_UTF8, csvDepuisLignes, type CelluleCsv } from '@imp/shared/csv';
import { messageErreur } from '@imp/shared/message-erreur';
import {
  calculerLigne,
  enHeuresDecimales,
  formatMinutes,
  joursSansReleve,
  type LigneReleve,
  type MotifAlerte,
  REGLE_PAUSE_DEFAUT,
  type ReglePause,
  recapParSalarie,
  totauxPeriode,
} from '@imp/shared/releve-heures';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Download,
  MessageSquarePlus,
  Plus,
  Trash2,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImportWhatsAppPanel } from '@/modules/rh/ImportWhatsAppPanel';
import { SaisieReleveForm } from '@/modules/rh/SaisieReleveForm';
import {
  getReglePause,
  listEmployesHeures,
  listReleves,
  type ReleveDetail,
  setReglePause,
  supprimerReleve,
} from '@/modules/rh/api';

function aujourdhui(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** `AAAA-MM` → bornes du mois, jours inclus. */
function bornesDuMois(ym: string): { du: string; au: string } {
  const [yStr, mStr] = ym.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  const dernier = new Date(y, m, 0).getDate();
  return { du: `${ym}-01`, au: `${ym}-${String(dernier).padStart(2, '0')}` };
}

const LIBELLE_ALERTE: Record<MotifAlerte, string> = {
  horaire_incoherent: 'Horaire incohérent',
  pause_non_declaree: 'Pause non déclarée',
  pause_declaree_ignoree: 'Pause déclarée ignorée',
  journee_courte: 'Journée courte',
};

const COULEUR_ALERTE: Record<MotifAlerte, string> = {
  horaire_incoherent: 'bg-destructive/10 text-destructive',
  pause_non_declaree: 'bg-amber-100 text-amber-800',
  pause_declaree_ignoree: 'bg-amber-100 text-amber-800',
  journee_courte: 'bg-sky-100 text-sky-800',
};

function jourCourt(jour: string): string {
  const [y, m, d] = jour.split('-').map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
}

export function RelevesHeuresPanel({ mois }: { mois: string }) {
  const { du, au } = bornesDuMois(mois);
  const qc = useQueryClient();
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [panneau, setPanneau] = useState<'aucun' | 'saisie' | 'import'>('aucun');
  const [aSupprimer, setASupprimer] = useState<string | null>(null);

  const releves = useQuery({
    queryKey: ['releves-heures', du, au],
    queryFn: () => listReleves(du, au),
  });
  const employes = useQuery({ queryKey: ['employes-heures'], queryFn: listEmployesHeures });
  const regleQ = useQuery({ queryKey: ['regle-pause'], queryFn: getReglePause });
  const regle: ReglePause = regleQ.data ?? REGLE_PAUSE_DEFAUT;

  const enregistrerRegle = useMutation({
    mutationFn: setReglePause,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regle-pause'] }),
  });
  const supprimer = useMutation({
    mutationFn: supprimerReleve,
    onSuccess: () => {
      setASupprimer(null);
      void qc.invalidateQueries({ queryKey: ['releves-heures'] });
    },
  });

  const lignes = useMemo(() => releves.data ?? [], [releves.data]);

  const { recap, totaux, manquants, parSalarie } = useMemo(() => {
    const r = recapParSalarie(lignes, regle);
    const groupes = new Map<string, ReleveDetail[]>();
    for (const l of lignes) {
      const cle = l.employeId ?? `nom:${l.nom.trim().toLowerCase()}`;
      groupes.set(cle, [...(groupes.get(cle) ?? []), l]);
    }
    for (const liste of groupes.values()) liste.sort((a, b) => a.jour.localeCompare(b.jour));
    return {
      recap: r,
      totaux: totauxPeriode(r),
      // Un jour qui n'est pas encore arrivé n'est pas un oubli : on s'arrête
      // à aujourd'hui, même quand on regarde le mois en cours.
      manquants: joursSansReleve(lignes, du, au < aujourdhui() ? au : aujourdhui()),
      parSalarie: groupes,
    };
  }, [lignes, regle, du, au]);

  const majRegle = (champs: Partial<ReglePause>): void => {
    enregistrerRegle.mutate({ ...regle, ...champs });
  };

  const exporter = (): void => {
    const entetes = [
      'Date',
      'Equipe',
      'Salarie',
      'Chantier',
      'Debut',
      'Fin',
      'Heures brutes',
      'Pause deduite',
      'Heures a payer',
      'Heures decimales',
      'Alertes',
    ];
    const corps: CelluleCsv[][] = [];
    for (const l of [...lignes].sort(
      (a, b) => a.jour.localeCompare(b.jour) || a.nom.localeCompare(b.nom, 'fr'),
    )) {
      const c = calculerLigne(l, regle);
      corps.push([
        l.jour,
        l.equipe ?? '',
        l.nom,
        l.chantierLibelle ?? '',
        formatMinutes(l.debut),
        formatMinutes(l.fin),
        c.brut === null ? '' : formatMinutes(c.brut),
        formatMinutes(c.pauseAppliquee),
        c.payable === null ? '' : formatMinutes(c.payable),
        c.payable === null ? '' : enHeuresDecimales(c.payable).toFixed(2).replace('.', ','),
        c.alertes.map((a) => LIBELLE_ALERTE[a]).join(' / '),
      ]);
    }
    corps.push([]);
    corps.push(['TOTAUX']);
    corps.push([
      'Salarie',
      'Jours',
      'Heures brutes',
      'Pauses deduites',
      'Heures a payer',
      'Decimal',
    ]);
    for (const s of recap) {
      corps.push([
        s.nom,
        s.jours,
        formatMinutes(s.brut),
        `-${formatMinutes(s.pause)}`,
        formatMinutes(s.payable),
        enHeuresDecimales(s.payable).toFixed(2).replace('.', ','),
      ]);
    }
    const csv = csvDepuisLignes([entetes, ...corps]);
    const url = URL.createObjectURL(new Blob([BOM_UTF8 + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `heures-${mois}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (releves.isError) {
    return <p className="text-destructive text-sm">{messageErreur(releves.error)}</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={panneau === 'saisie' ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => {
            setPanneau(panneau === 'saisie' ? 'aucun' : 'saisie');
          }}
        >
          <Plus className="mr-1.5 h-4 w-4" /> Ajouter une journée
        </Button>
        <Button
          variant={panneau === 'import' ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => {
            setPanneau(panneau === 'import' ? 'aucun' : 'import');
          }}
        >
          <MessageSquarePlus className="mr-1.5 h-4 w-4" /> Importer depuis WhatsApp
        </Button>
        <Button variant="outline" size="sm" onClick={exporter} disabled={lignes.length === 0}>
          <Download className="mr-1.5 h-4 w-4" /> Exporter le mois
        </Button>
      </div>

      {panneau === 'saisie' ? (
        <SaisieReleveForm
          employes={employes.data ?? []}
          mois={mois}
          onEnregistre={() => {
            setPanneau('aucun');
            void qc.invalidateQueries({ queryKey: ['releves-heures'] });
          }}
        />
      ) : null}

      {panneau === 'import' ? (
        <ImportWhatsAppPanel
          employes={employes.data ?? []}
          onImporte={() => {
            setPanneau('aucun');
            void qc.invalidateQueries({ queryKey: ['releves-heures'] });
          }}
        />
      ) : null}

      <ReglePauseCard regle={regle} onChange={majRegle} enCours={enregistrerRegle.isPending} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Tuile titre="Heures brutes" valeur={formatMinutes(totaux.brut)} />
        <Tuile titre="Pauses déduites" valeur={`-${formatMinutes(totaux.pause)}`} />
        <Tuile titre="À payer" valeur={formatMinutes(totaux.payable)} fort />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Récapitulatif par salarié</CardTitle>
        </CardHeader>
        <CardContent>
          {releves.isLoading ? (
            <p className="text-muted-foreground text-sm">Chargement…</p>
          ) : recap.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Aucun relevé sur ce mois. Importez les messages WhatsApp ou ajoutez une journée.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                    <th className="w-8 py-2" />
                    <th className="py-2 pr-3">Salarié</th>
                    <th className="py-2 pr-3 text-right">Jours</th>
                    <th className="py-2 pr-3 text-right">Brut</th>
                    <th className="py-2 pr-3 text-right">Pauses</th>
                    <th className="py-2 pr-3 text-right">À payer</th>
                    <th className="py-2 pr-3 text-right">Décimal</th>
                    <th className="py-2 text-right">À vérifier</th>
                  </tr>
                </thead>
                <tbody>
                  {recap.map((s) => {
                    const cle = s.employeId ?? `nom:${s.nom.trim().toLowerCase()}`;
                    const deplie = ouvert === cle;
                    return (
                      <Fragment key={cle}>
                        <tr className="border-b">
                          <td className="py-2">
                            <button
                              type="button"
                              aria-label={deplie ? 'Replier le détail' : 'Voir le détail'}
                              className="hover:bg-accent rounded p-1"
                              onClick={() => {
                                setOuvert(deplie ? null : cle);
                              }}
                            >
                              {deplie ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td className="py-2 pr-3 font-medium">
                            {s.nom}
                            {s.employeId === null ? (
                              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                                à rattacher
                              </span>
                            ) : null}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">{s.jours}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatMinutes(s.brut)}
                          </td>
                          <td className="text-muted-foreground py-2 pr-3 text-right tabular-nums">
                            -{formatMinutes(s.pause)}
                          </td>
                          <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                            {formatMinutes(s.payable)}
                          </td>
                          <td className="text-muted-foreground py-2 pr-3 text-right tabular-nums">
                            {enHeuresDecimales(s.payable).toFixed(2)}
                          </td>
                          <td className="py-2 text-right tabular-nums">
                            {s.joursAvecAlerte === 0 ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                                {s.joursAvecAlerte} jour{s.joursAvecAlerte > 1 ? 's' : ''}
                              </span>
                            )}
                          </td>
                        </tr>
                        {deplie ? (
                          <tr className="bg-muted/30 border-b">
                            <td colSpan={8} className="p-0">
                              <DetailSalarie
                                lignes={parSalarie.get(cle) ?? []}
                                regle={regle}
                                aSupprimer={aSupprimer}
                                onDemandeSuppression={setASupprimer}
                                onSupprime={(id) => {
                                  supprimer.mutate(id);
                                }}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td />
                    <td className="py-2 pr-3">Total</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{totaux.jours}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {formatMinutes(totaux.brut)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      -{formatMinutes(totaux.pause)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {formatMinutes(totaux.payable)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {enHeuresDecimales(totaux.payable).toFixed(2)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
              {totaux.joursIllisibles > 0 ? (
                <p className="text-destructive mt-3 flex items-start gap-2 text-sm">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  {totaux.joursIllisibles} relevé{totaux.joursIllisibles > 1 ? 's ont' : ' a'} un
                  horaire incohérent et n&apos;{totaux.joursIllisibles > 1 ? 'entrent' : 'entre'}{' '}
                  pas dans les totaux. Corrigez-l
                  {totaux.joursIllisibles > 1 ? 'es' : 'e'} avant d&apos;envoyer la paie.
                </p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Jours sans relevé</CardTitle>
        </CardHeader>
        <CardContent>
          {manquants.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {lignes.length === 0
                ? 'Rien à vérifier tant qu’aucun relevé n’est saisi.'
                : 'Chaque équipe a déclaré tous les jours ouvrés du mois.'}
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {manquants.map((m) => (
                <li key={m.equipe} className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">Équipe {m.equipe}</span>
                  <span className="text-muted-foreground">
                    {m.jours.length} jour{m.jours.length > 1 ? 's' : ''} :
                  </span>
                  {m.jours.map((j) => (
                    <span
                      key={j}
                      className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                    >
                      {jourCourt(j)}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Tuile({ titre, valeur, fort }: { titre: string; valeur: string; fort?: boolean }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-muted-foreground text-xs uppercase">{titre}</p>
        <p className={`mt-1 tabular-nums ${fort === true ? 'text-2xl font-bold' : 'text-xl'}`}>
          {valeur}
        </p>
      </CardContent>
    </Card>
  );
}

function ReglePauseCard({
  regle,
  onChange,
  enCours,
}: {
  regle: ReglePause;
  onChange: (champs: Partial<ReglePause>) => void;
  enCours: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Règle de pause</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm">
          Les équipes déclarent des horaires, pas des heures payables. Ce réglage dit ce qui est
          retiré de chaque journée — et il s&apos;applique à tout l&apos;écran, totaux et export
          compris.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label htmlFor="pause_defaut">Pause déduite par jour</Label>
            <div className="flex items-center gap-2">
              <Input
                id="pause_defaut"
                type="number"
                min={0}
                max={720}
                step={15}
                className="w-28"
                value={regle.defautMinutes}
                disabled={enCours}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v) && v >= 0 && v <= 720) onChange({ defautMinutes: v });
                }}
              />
              <span className="text-muted-foreground text-sm">minutes</span>
            </div>
          </div>
        </div>
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={regle.respecterPauseDeclaree}
            disabled={enCours}
            onChange={(e) => {
              onChange({ respecterPauseDeclaree: e.target.checked });
            }}
          />
          <span>
            <span className="font-medium">Respecter la pause écrite par l&apos;équipe.</span>{' '}
            <span className="text-muted-foreground">
              Décoché, la règle ci-dessus s&apos;applique même les jours où « sans pause » a été
              écrit.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={!regle.deduireSurJourneeCourte}
            disabled={enCours}
            onChange={(e) => {
              onChange({ deduireSurJourneeCourte: !e.target.checked });
            }}
          />
          <span>
            <span className="font-medium">Ne rien retirer aux journées courtes</span>{' '}
            <span className="text-muted-foreground">
              (celles qui finissent à {formatMinutes(regle.finJourneeCourte)} ou avant : il n&apos;y
              a pas eu de coupure déjeuner).
            </span>
          </span>
        </label>
      </CardContent>
    </Card>
  );
}

function DetailSalarie({
  lignes,
  regle,
  aSupprimer,
  onDemandeSuppression,
  onSupprime,
}: {
  lignes: readonly ReleveDetail[];
  regle: ReglePause;
  aSupprimer: string | null;
  onDemandeSuppression: (id: string | null) => void;
  onSupprime: (id: string) => void;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-muted-foreground border-b text-left text-xs uppercase">
          <th className="py-2 pl-10 pr-3">Jour</th>
          <th className="py-2 pr-3">Chantier</th>
          <th className="py-2 pr-3">Horaires</th>
          <th className="py-2 pr-3 text-right">Brut</th>
          <th className="py-2 pr-3 text-right">Pause</th>
          <th className="py-2 pr-3 text-right">À payer</th>
          <th className="py-2 pr-3">À vérifier</th>
          <th className="w-24 py-2" />
        </tr>
      </thead>
      <tbody>
        {lignes.map((l) => {
          const c: LigneReleve = calculerLigne(l, regle);
          return (
            <tr key={l.id} className="border-b last:border-0">
              <td className="py-1.5 pl-10 pr-3 tabular-nums">{jourCourt(l.jour)}</td>
              <td className="text-muted-foreground py-1.5 pr-3">{l.chantierLibelle ?? '—'}</td>
              <td className="py-1.5 pr-3 tabular-nums">
                {formatMinutes(l.debut)} → {formatMinutes(l.fin)}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {c.brut === null ? '—' : formatMinutes(c.brut)}
              </td>
              <td className="text-muted-foreground py-1.5 pr-3 text-right tabular-nums">
                -{formatMinutes(c.pauseAppliquee)}
                {l.pauseDeclaree !== null ? (
                  <span className="ml-1 text-xs">(dit : {formatMinutes(l.pauseDeclaree)})</span>
                ) : null}
              </td>
              <td className="py-1.5 pr-3 text-right font-medium tabular-nums">
                {c.payable === null ? (
                  <span className="text-destructive">à corriger</span>
                ) : (
                  formatMinutes(c.payable)
                )}
              </td>
              <td className="py-1.5 pr-3">
                <div className="flex flex-wrap gap-1">
                  {c.alertes.map((a) => (
                    <span key={a} className={`rounded px-1.5 py-0.5 text-xs ${COULEUR_ALERTE[a]}`}>
                      {LIBELLE_ALERTE[a]}
                    </span>
                  ))}
                </div>
              </td>
              <td className="py-1.5 text-right">
                {aSupprimer === l.id ? (
                  <span className="flex items-center justify-end gap-1">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        onSupprime(l.id);
                      }}
                    >
                      Supprimer
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        onDemandeSuppression(null);
                      }}
                    >
                      Non
                    </Button>
                  </span>
                ) : (
                  <button
                    type="button"
                    aria-label={`Supprimer le relevé du ${l.jour}`}
                    title={l.sourceTexte ?? undefined}
                    className="text-muted-foreground hover:bg-accent hover:text-destructive rounded p-1.5"
                    onClick={() => {
                      onDemandeSuppression(l.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
